import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Table, Spinner, Modal, Badge, Form } from "react-bootstrap";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";

import * as signalR from "@microsoft/signalr";

const STATUS_COLORS = { 0: "warning", 1: "success", 2: "danger" };
const STATUS_LABELS = { 0: "Pending", 1: "Approved", 2: "Rejected" };

const formatCurrency = (val) => {
  const n = parseFloat(val) || 0;
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDate = (d) => {
  if (!d) return "-";
  try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
};

const WithdrawalRequests = () => {
  const dispatch = useDispatch();
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Approve modal
  const [approveModal, setApproveModal] = useState({ show: false, row: null });
  // Reject modal
  const [rejectModal, setRejectModal] = useState({ show: false, row: null, reason: "" });

  const LIST_URL = `${API_WEB_URLS.MASTER}/0/token/WithdrawalRequestList/Id/0`;
  const APPROVAL_URL = `${API_WEB_URLS.BASE}WithdrawalApproval/0/token`;

  const fetchData = useCallback(() => {
    setLoading(true);
    Fn_FillListData(dispatch, setGridData, "gridData", LIST_URL)
      .finally(() => setLoading(false));
  }, [dispatch, LIST_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Setup SignalR real-time auto-refresh connection
  useEffect(() => {
    const hubUrl = API_WEB_URLS.BASE.replace("/api/V1/", "/bookingHub").replace("/api/v1/", "/bookingHub");
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .build();

    connection.on("WithdrawalRequestUpdated", () => {
      console.log("Real-time SignalR update: Withdrawal requests updated. Reloading list...");
      fetchData();
    });

    connection.start()
      .then(() => {
        console.log("Withdrawal Requests page connected to SignalR Hub successfully.");
      })
      .catch(err => console.error("SignalR connection error:", err));

    return () => {
      connection.stop();
    };
  }, [fetchData]);

  const handleApprove = async () => {
    if (!approveModal.row) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("Id", approveModal.row.Id);
      formData.append("Status", "1");
      formData.append("RejectionReason", "");
      formData.append("ActionBy", "1");

      const token = JSON.parse(localStorage.getItem("Token") || "null");
      const res = await fetch(APPROVAL_URL, {
        method: "POST",
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (json?.Success || json?.success) {
        setApproveModal({ show: false, row: null });
        fetchData();
      } else {
        alert("Error: " + (json?.Message || json?.message || "Could not approve"));
      }
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.row) return;
    if (!rejectModal.reason.trim()) { alert("Please enter a rejection reason."); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("Id", rejectModal.row.Id);
      formData.append("Status", "2");
      formData.append("RejectionReason", rejectModal.reason.trim());
      formData.append("ActionBy", "1");

      const token = JSON.parse(localStorage.getItem("Token") || "null");
      const res = await fetch(APPROVAL_URL, {
        method: "POST",
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (json?.Success || json?.success) {
        setRejectModal({ show: false, row: null, reason: "" });
        fetchData();
      } else {
        alert("Error: " + (json?.Message || json?.message || "Could not reject"));
      }
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportExcel = () => {
    if (data.length === 0) return;

    let csvContent = "";
    csvContent += `"Driver Withdrawal Requests Report"\n`;
    csvContent += `"Generated On","${new Date().toLocaleString("en-IN")}"\n`;
    csvContent += `\n`;

    const headers = ["Request ID", "Driver Name", "Mobile", "Bank Name", "Account No", "IFSC", "Account Holder", "Wallet Balance", "Requested Amount", "Status", "Rejection Reason", "Requested On", "Actioned On"];
    csvContent += headers.map(h => `"${h}"`).join(",") + "\n";

    data.forEach((r) => {
      const statusText = r.Status === 0 ? "Pending" : r.Status === 1 ? "Approved" : "Rejected";
      const row = [
        r.Id.toString(),
        r.DriverName || "-",
        r.MobileNo || "-",
        r.BankName || "-",
        r.AccountNo || "-",
        r.IFSC || "-",
        r.AccountPersonName || r.DriverName || "-",
        (r.CurrentWalletBalance || 0).toFixed(2),
        (r.RequestedAmount || 0).toFixed(2),
        statusText,
        r.RejectionReason || "-",
        r.RequestedOn || "-",
        r.ActionOn || "-"
      ];
      csvContent += row.map(val => `"${val.replace(/"/g, '""')}"`).join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Withdrawal_Requests_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (data.length === 0) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup blocked! Please allow popups to export PDF.");
      return;
    }

    const title = "Driver Withdrawal Requests Report";
    let rowsHtml = "";

    data.forEach((r, idx) => {
      const statusText = r.Status === 0 ? "Pending" : r.Status === 1 ? "Approved" : "Rejected";
      const statusColor = r.Status === 0 ? "#D97706" : r.Status === 1 ? "#16A34A" : "#DC2626";
      const bankDetails = (r.BankName || r.AccountNo) 
        ? `${r.BankName || 'Bank'} | A/C: ${r.AccountNo || '-'} | IFSC: ${r.IFSC || '-'}`
        : '-';
      rowsHtml += `
        <tr>
          <td>${idx + 1}</td>
          <td>#${r.Id}</td>
          <td>${r.DriverName || "-"}</td>
          <td>${r.MobileNo || "-"}</td>
          <td>${bankDetails}</td>
          <td style="text-align: right;">₹${(r.CurrentWalletBalance || 0).toFixed(2)}</td>
          <td style="text-align: right; font-weight: bold; color: #1D4ED8;">₹${(r.RequestedAmount || 0).toFixed(2)}</td>
          <td style="font-weight: bold; color: ${statusColor};">${statusText}</td>
          <td style="color: #DC2626;">${r.RejectionReason || "-"}</td>
          <td>${formatDate(r.RequestedOn)}</td>
          <td>${formatDate(r.ActionOn)}</td>
        </tr>
      `;
    });

    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
            .header { display: flex; justify-content: space-between; border-bottom: 2px solid #E87B2D; padding-bottom: 10px; margin-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; color: #E87B2D; }
            .report-title { font-size: 18px; font-weight: bold; text-align: right; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th, td { border: 1px solid #dee2e6; padding: 8px; text-align: left; }
            th { background-color: #E87B2D; color: white; text-transform: uppercase; font-size: 11px; }
            @media print {
              body { padding: 0; }
              th { background-color: #E87B2D !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">KIRAYAGO</div>
            <div class="report-title">Driver Withdrawal Requests</div>
          </div>
          <div style="margin-bottom: 20px; font-size: 12px; color: #666;">
            Generated On: ${new Date().toLocaleString("en-IN")} | Total Requests: ${data.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Request ID</th>
                <th>Driver Name</th>
                <th>Mobile</th>
                <th>Bank Account Details</th>
                <th style="text-align: right;">Wallet Balance</th>
                <th style="text-align: right;">Requested Amount</th>
                <th>Status</th>
                <th>Rejection Reason</th>
                <th>Requested On</th>
                <th>Actioned On</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const data = useMemo(() => gridData || [], [gridData]);

  const columns = useMemo(() => [
    {
      Header: "#",
      id: "row",
      disableSortBy: true,
      Cell: ({ row }) => <span style={{ fontSize: 11 }}>{row.index + 1}</span>,
    },
    { Header: "Request ID", accessor: "Id", Cell: ({ value }) => <span style={{ fontSize: 11, fontWeight: 600 }}>#{value}</span> },
    { Header: "Driver Name", accessor: "DriverName", Cell: ({ value }) => <span style={{ fontSize: 12 }}>{value || "-"}</span> },
    { Header: "Mobile", accessor: "MobileNo", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
    {
      Header: "Bank Account Details",
      accessor: "BankName",
      Cell: ({ row }) => {
        const r = row.original;
        if (!r.BankName && !r.AccountNo) {
          return <span className="text-muted" style={{ fontSize: 11, fontStyle: "italic" }}>Not provided</span>;
        }
        return (
          <div style={{ fontSize: 11, lineHeight: 1.35, minWidth: 145 }}>
            <div><strong style={{ color: "#1F2937" }}>{r.BankName || "Bank"}</strong></div>
            <div style={{ color: "#2563EB", fontWeight: 600 }}>A/C: {r.AccountNo || "-"}</div>
            <div style={{ color: "#4B5563", fontSize: 10 }}>IFSC: {r.IFSC || "-"}</div>
            {r.AccountPersonName && <div style={{ color: "#6B7280", fontSize: 10 }}>Holder: {r.AccountPersonName}</div>}
          </div>
        );
      },
    },
    {
      Header: "Wallet Balance",
      accessor: "CurrentWalletBalance",
      Cell: ({ value }) => (
        <span style={{ fontSize: 11, color: value >= 0 ? "#16A34A" : "#DC2626", fontWeight: 600 }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: "Requested Amount",
      accessor: "RequestedAmount",
      Cell: ({ value }) => (
        <span style={{ fontSize: 12, fontWeight: 700, color: "#1D4ED8" }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: "Status",
      accessor: "Status",
      Cell: ({ value }) => (
        <Badge bg={STATUS_COLORS[value] || "secondary"} style={{ fontSize: 10 }}>
          {STATUS_LABELS[value] || "Unknown"}
        </Badge>
      ),
    },
    {
      Header: "Rejection Reason",
      accessor: "RejectionReason",
      Cell: ({ value }) => (
        <span style={{ fontSize: 11, color: "#DC2626" }}>{value || "-"}</span>
      ),
    },
    { Header: "Requested On", accessor: "RequestedOn", Cell: ({ value }) => <span style={{ fontSize: 10 }}>{formatDate(value)}</span> },
    { Header: "Actioned On", accessor: "ActionOn", Cell: ({ value }) => <span style={{ fontSize: 10 }}>{formatDate(value)}</span> },
    {
      Header: "Action",
      id: "action",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isPending = row.original.Status === 0;
        if (!isPending) {
          return <span style={{ fontSize: 11, color: "#666", fontStyle: "italic" }}>Processed</span>;
        }
        return (
          <div style={{ display: "flex", gap: 4 }}>
            <Button
              size="sm"
              variant="outline-success"
              className="py-0 px-1"
              style={{ fontSize: 11 }}
              onClick={() => setApproveModal({ show: true, row: row.original })}
            >
              ✓ Approve
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="py-0 px-1"
              style={{ fontSize: 11 }}
              onClick={() => setRejectModal({ show: true, row: row.original, reason: "" })}
            >
              ✗ Reject
            </Button>
          </div>
        );
      },
    },
  ], []);

  const {
    getTableProps, getTableBodyProps, headerGroups, prepareRow,
    page, canPreviousPage, canNextPage, pageOptions, pageCount,
    gotoPage, nextPage, previousPage, setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    { columns, data, initialState: { pageIndex: 0, pageSize: 10, sortBy: [{ id: "RequestedOn", desc: true }] } },
    useGlobalFilter, useSortBy, usePagination
  );

  if (loading) {
    return (
      <div style={{ padding: "6px 8px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div style={{ padding: "6px 8px", maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, paddingBottom: 4, borderBottom: "2px solid #E87B2D" }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>💸 Driver Withdrawal Requests</span>
          <span style={{ fontSize: 11, color: "#888", marginLeft: 8 }}>
            {data.filter(r => r.Status === 0).length} pending
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 150, height: 26, fontSize: 12 }}
          />
          <Button size="sm" variant="outline-secondary" style={{ fontSize: 11, height: 26, padding: "0 8px" }} onClick={fetchData}>
            ↻ Refresh
          </Button>
          {data.length > 0 && (
            <>
              <Button
                size="sm"
                variant="outline-success"
                onClick={handleExportExcel}
                style={{ fontSize: 11, height: 26, padding: "0 8px", display: "flex", alignItems: "center", gap: 4 }}
              >
                <i className="bx bx-file" style={{ fontSize: 13 }}></i> Excel
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={handleExportPDF}
                style={{ fontSize: 11, height: 26, padding: "0 8px", display: "flex", alignItems: "center", gap: 4 }}
              >
                <i className="bx bx-printer" style={{ fontSize: 13 }}></i> PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        {[
          { label: "Total Requests", value: data.length, color: "#1D4ED8" },
          { label: "Pending", value: data.filter(r => r.Status === 0).length, color: "#D97706" },
          { label: "Approved", value: data.filter(r => r.Status === 1).length, color: "#16A34A" },
          { label: "Rejected", value: data.filter(r => r.Status === 2).length, color: "#DC2626" },
          { label: "Approved Amount", value: formatCurrency(data.filter(r => r.Status === 1).reduce((a, b) => a + (parseFloat(b.RequestedAmount) || 0), 0)), color: "#16A34A" },
        ].map((t, i) => (
          <div key={i} style={{ background: "#fff", border: `1px solid ${t.color}30`, borderRadius: 6, padding: "6px 14px", minWidth: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 10, color: "#666", marginBottom: 2 }}>{t.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.color }}>{t.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", marginBottom: 4 }}>
        <Table {...getTableProps()} size="sm" bordered hover style={{ fontSize: 12, minWidth: 1100 }}>
          <thead>
            {headerGroups.map((hg, i) => (
              <tr {...hg.getHeaderGroupProps()} key={i}>
                {hg.headers.map((col, j) => (
                  <th
                    {...col.getHeaderProps(col.disableSortBy ? {} : col.getSortByToggleProps())}
                    key={j}
                    style={{ padding: "4px 6px", fontSize: 11, fontWeight: 600, backgroundColor: "#E87B2D", color: "#fff", whiteSpace: "nowrap" }}
                  >
                    {col.render("Header")}
                    {!col.disableSortBy && col.isSorted && <span style={{ marginLeft: 2 }}>{col.isSortedDesc ? "▼" : "▲"}</span>}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-3 text-muted" style={{ fontSize: 12 }}>No withdrawal requests found.</td></tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                const isPending = row.original.Status === 0;
                return (
                  <tr
                    {...row.getRowProps()}
                    key={i}
                    style={{ background: isPending ? "#FFFBEB" : "transparent" }}
                  >
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j} style={{ padding: "4px 6px", verticalAlign: "middle" }}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, flexWrap: "wrap", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Page {pageIndex + 1} of {pageOptions.length || 1}</span>
          <select className="form-select form-select-sm" style={{ width: 50, height: 24, fontSize: 11 }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => gotoPage(0)} disabled={!canPreviousPage}>|«</button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => previousPage()} disabled={!canPreviousPage}>‹</button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => nextPage()} disabled={!canNextPage}>›</button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>»|</button>
        </div>
      </div>

      {/* APPROVE MODAL */}
      <Modal show={approveModal.show} onHide={() => !submitting && setApproveModal({ show: false, row: null })} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#16A34A", color: "#fff" }}>
          <Modal.Title style={{ fontSize: 15 }}>✓ Approve Withdrawal Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approveModal.row && (
            <div>
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#666" }}>Driver</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{approveModal.row.DriverName}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#666" }}>Mobile</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{approveModal.row.MobileNo || "-"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#666" }}>Wallet Balance</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#16A34A" }}>{formatCurrency(approveModal.row.CurrentWalletBalance)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>Withdrawal Amount</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#1D4ED8" }}>{formatCurrency(approveModal.row.RequestedAmount)}</span>
                </div>
              </div>

              {/* Captain Bank Account Details */}
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1E40AF", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                  <span>🏦 Captain Bank Account Details (For Transfer)</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", fontSize: 11 }}>
                  <div>
                    <span style={{ color: "#6B7280" }}>Bank Name:</span><br />
                    <strong style={{ color: "#1F2937" }}>{approveModal.row.BankName || "-"}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#6B7280" }}>Account Number:</span><br />
                    <strong style={{ color: "#2563EB", fontSize: 12 }}>{approveModal.row.AccountNo || "-"}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#6B7280" }}>IFSC Code:</span><br />
                    <strong style={{ color: "#1F2937" }}>{approveModal.row.IFSC || "-"}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#6B7280" }}>Account Holder:</span><br />
                    <strong style={{ color: "#1F2937" }}>{approveModal.row.AccountPersonName || approveModal.row.DriverName || "-"}</strong>
                  </div>
                </div>
              </div>
              <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#92400E" }}>
                <strong>Accounting Entry:</strong><br />
                Dr: Driver Wallet Ledger → {formatCurrency(approveModal.row.RequestedAmount)}<br />
                Cr: Company Bank (Id 32) → {formatCurrency(approveModal.row.RequestedAmount)}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setApproveModal({ show: false, row: null })} disabled={submitting}>Cancel</Button>
          <Button variant="success" size="sm" onClick={handleApprove} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : "✓ Confirm Approve & Post Voucher"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* REJECT MODAL */}
      <Modal show={rejectModal.show} onHide={() => !submitting && setRejectModal({ show: false, row: null, reason: "" })} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#DC2626", color: "#fff" }}>
          <Modal.Title style={{ fontSize: 15 }}>✗ Reject Withdrawal Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejectModal.row && (
            <div>
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#666" }}>Driver</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{rejectModal.row.DriverName}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, color: "#666" }}>Requested Amount</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#DC2626" }}>{formatCurrency(rejectModal.row.RequestedAmount)}</span>
                </div>
              </div>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Rejection Reason <span style={{ color: "red" }}>*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter reason for rejection (visible to driver)..."
                  value={rejectModal.reason}
                  onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
                  style={{ fontSize: 12 }}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setRejectModal({ show: false, row: null, reason: "" })} disabled={submitting}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleReject} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : "✗ Confirm Reject"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WithdrawalRequests;
